import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  IconButton,
  Stack,
  Button,
  Typography,
  Tooltip,
  keyframes,
  Divider,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import StopIcon from "@mui/icons-material/Stop";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import BoltIcon from "@mui/icons-material/Bolt";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import ReactECharts from "echarts-for-react";
import Swal from "sweetalert2";
import {
  FaCartShopping,
  FaFileLines,
  FaRegLightbulb,
  FaCircleCheck,
  FaCircleXmark,
  FaRegThumbsUp,
  FaThumbsUp,
  FaRegThumbsDown,
  FaThumbsDown,
} from "react-icons/fa6";
import { useSite } from "../../context/siteContext";
import { useCart } from "../../context/cartContext";
import { getProductsById } from "../../services/cartService";
import { getSiteAddressesNew } from "../../services/manageSitesService";
import { createOrder } from "../../services/checkoutService";
import { useAuth } from "../../context/authContext";
import {
  askChatbot,
  clearChatbotSession,
  getChatbotStatus,
  submitChatbotFeedback,
  streamChatbot,
} from "../../services/chatBotService";
import TopProductListItem from "../TopProductListItem/TopProductListItem";
import MachineListItem from "../MachineListItem/MachineListItem";
import OrderListItem from "../OrderListItem/OrderListItem";
import DocCard from "../DocCard/DocCard";
import { SERVER_BASE_URL } from "../../config";
import { ROUTES } from "../../constants";
import "./ChatBot.css";

const pulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
`;

const STAGE_MESSAGES = [
  "Looking into it...",
  "Checking your account details...",
  "Finding the right information...",
  "Preparing your answer...",
  "Almost done...",
];

const DEFAULT_QUESTIONS = [
  "Add my most ordered parts to cart",
  "Show my recent order summary",
  "Show my request list",
  "Track my latest order",
  "Show top ordered parts",
  "Show relevant documents",
];
const CHIP_ICON = "/chip-icon.png";

const getInitialQuestionsForPath = (pathname = "") => {
  const path = String(pathname || "").toLowerCase();

  if (path.includes("/cart")) {
    return [
      "Show my cart summary",
      "Add my most ordered parts to cart",
      "Move unavailable parts to request list",
      "Recommend complementary parts for my cart",
      "Update quantities in my cart",
      "Prepare this cart for checkout",
    ];
  }

  if (path.includes("/request") || path.includes("/rfq")) {
    return [
      "Show my request list summary",
      "Move approved request list items to cart",
      "Create a request list from my recent order",
      "Show request list items by machine",
      "Prioritize urgent request list items",
      "What items are waiting for quotation?",
    ];
  }

  if (path.includes("/order")) {
    return [
      "Show my latest order status",
      "Reorder all parts from my last order",
      "Show top ordered parts in the last 90 days",
      "Track shipment for my open orders",
      "Show delayed order lines",
      "Create a quick reorder list",
    ];
  }

  if (path.includes("/search") || path.includes("/product")) {
    return [
      "Find parts compatible with my selected machine",
      "Show alternatives for this part",
      "Add this part to cart",
      "Show documents for this part",
      "Check if this part is purchasable",
      "Recommend related products",
    ];
  }

  if (path.includes("/doc") || path.includes("/knowledge")) {
    return [
      "Show documents for my top ordered parts",
      "Find installation guide for this part",
      "Show safety and compliance documents",
      "List troubleshooting docs for this machine",
      "Open the latest datasheet",
      "Summarize this document for ordering impact",
    ];
  }

  return DEFAULT_QUESTIONS;
};

const getDocHref = (doc) => {
  const type = String(doc?.attachment_type || "").toLowerCase();
  if (type === "url" && doc?.attachment) return doc.attachment;
  if (type === "file" && doc?.attachment) return `${SERVER_BASE_URL}/${doc.attachment}`;
  return "";
};

const getDocChipLabel = (doc) => {
  const type = String(doc?.attachment_type || "").toLowerCase();
  if (type === "url") return "URL";
  if (type === "file") {
    const path = doc?.attachment || doc?.name || "";
    const ext = (path.split(".").pop() || "").toLowerCase();
    if (!ext) return "FILE";
    const map = {
      pdf: "PDF",
      png: "PNG",
      jpg: "JPG",
      jpeg: "JPG",
      csv: "CSV",
      xls: "Excel",
      xlsx: "Excel",
      doc: "Word",
      docx: "Word",
      ppt: "PPT",
      pptx: "PPT",
    };
    return map[ext] || ext.toUpperCase();
  }
  return "DOC";
};

const normalizeOrderEntry = (row) => {
  if (!row) return null;
  if (row.order || row.products) {
    const orderNumber = row?.order?.order_number || row?.order_number || row?.name || "Order";
    return {
      ...row,
      name: orderNumber,
      order_number: orderNumber,
      order_id: row?.order?.order_id || row?.order_id || row?.product_id,
      order_po: row?.details?.po_number || row?.order_po || null,
    };
  }
  if (row.order_number || row.order_id) {
    return {
      ...row,
      name: row.order_number || row.name || "Order",
      order_id: row.order_id || row.product_id,
    };
  }
  return null;
};

const normalizeRequestRowToProduct = (row) => {
  if (!row) return null;
  if (row?.productData) return row.productData;
  if (row?.product_id && (row?.product_name || row?.product_number)) return row;
  return null;
};

const normalizeRequestRow = (row, bucket) => {
  if (!row) return null;
  const product = normalizeRequestRowToProduct(row);
  if (!product) return null;
  const machineId = Number(row?.machine_id || row?.machineData?.machine_id || product?.machine_id || 0) || null;
  const machineName = row?.machineData?.machine_name || product?.machine_name || "";
  const machineNumber = row?.machineData?.machine_number || product?.machine_number || "";
  return {
    bucket,
    machine_id: machineId,
    machine_name: machineName,
    machine_number: machineNumber,
    product,
  };
};

const inferActionType = (action = {}) => {
  const explicit = String(action?.action_type || action?.type || "").toLowerCase();
  if (explicit === "agentic" || explicit === "question") return explicit;

  const prompt = String(action?.prompt || "").toLowerCase();
  if (!prompt) return "question";
  if (prompt === "yes" || prompt === "no") return "agentic";
  if (/^(show|what|which|where|when|why|how|list|find|track|open)\b/.test(prompt)) return "question";
  if (/\?$/.test(prompt)) return "question";
  return "agentic";
};

const GENERIC_ACTION_TEXT = new Set([
  "ask",
  "action",
  "question",
  "questions",
  "actions",
]);

const normalizeAssistantActions = (rawActions = []) => {
  const seen = new Set();
  return (Array.isArray(rawActions) ? rawActions : [])
    .map((raw) => (typeof raw === "string" ? { prompt: raw } : raw))
    .filter(Boolean)
    .map((act) => {
      const label = String(act?.label || "").trim();
      const prompt = String(act?.prompt || "").trim();
      const path = String(act?.path || act?.route || "").trim();
      return { ...act, label, prompt, path };
    })
    .filter((act) => {
      if (!act.prompt && !act.label && !act.path) return false;
      const labelLower = act.label.toLowerCase();
      const promptLower = act.prompt.toLowerCase();
      if (GENERIC_ACTION_TEXT.has(labelLower) && !act.prompt && !act.path) return false;
      if (GENERIC_ACTION_TEXT.has(promptLower) && !act.label && !act.path) return false;
      return true;
    })
    .filter((act) => {
      const key = `${inferActionType(act)}|${act.label.toLowerCase()}|${act.prompt.toLowerCase()}|${act.path.toLowerCase()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 8);
};

const getIconForPrompt = (text = "", type = "question") => {
  const prompt = String(text || "").toLowerCase();
  if (prompt === "yes" || prompt.includes("confirm")) return <FaCircleCheck size={14} />;
  if (prompt === "no" || prompt.includes("cancel")) return <FaCircleXmark size={14} />;
  if (prompt.includes("cart") || prompt.includes("checkout")) return <FaCartShopping size={14} />;
  if (prompt.includes("request list") || prompt.includes("request")) return <RequestQuoteIcon fontSize="small" />;
  if (prompt.includes("order") || prompt.includes("track") || prompt.includes("shipment")) return <LocalShippingIcon fontSize="small" />;
  if (prompt.includes("support") || prompt.includes("case")) return <SupportAgentIcon fontSize="small" />;
  if (prompt.includes("document") || prompt.includes("manual") || prompt.includes("knowledge")) return <FaFileLines size={14} />;
  if (prompt.includes("recommend")) return <FaRegLightbulb size={14} />;
  if (prompt.includes("part") || prompt.includes("product") || prompt.includes("machine")) return <Inventory2Icon fontSize="small" />;
  return type === "agentic" ? <BoltIcon fontSize="small" /> : <HelpOutlineIcon fontSize="small" />;
};

const getChartOption = (chart = {}) => {
  const x = Array.isArray(chart?.x) ? chart.x : [];
  const series = Array.isArray(chart?.series) ? chart.series : [];
  const isBar = String(chart?.type || "").toLowerCase() === "bar";
  const currency = String(chart?.currency || "USD");

  return {
    grid: { left: 44, right: 18, top: 34, bottom: 30 },
    tooltip: {
      trigger: "axis",
      valueFormatter: (value) => {
        if (chart?.y_format === "currency") return `${currency} ${Number(value || 0).toLocaleString()}`;
        return Number(value || 0).toLocaleString();
      },
    },
    xAxis: { type: "category", data: x, axisLabel: { fontSize: 10 } },
    yAxis: { type: "value", axisLabel: { fontSize: 10 } },
    series: series.map((s) => ({
      name: s?.name || "Series",
      type: isBar ? "bar" : "line",
      smooth: !isBar,
      data: Array.isArray(s?.data) ? s.data : [],
      areaStyle: isBar ? undefined : { opacity: 0.15 },
    })),
    legend: { top: 0, textStyle: { fontSize: 11 } },
  };
};

const normalizeActionPath = (value) => {
  const raw = String(value || "").trim();
  if (!raw || raw.startsWith("http://") || raw.startsWith("https://")) return null;
  const lower = raw.toLowerCase();

  if (lower === "/request-list") return ROUTES.REQUESTS;
  if (lower === "/saved-for-later") return ROUTES.SAVED_FOR_LATER;

  return raw.startsWith("/") ? raw : `/${raw}`;
};

const resolveActionRoute = (action = {}, pathname = "") => {
  const explicitRoute = [
    action?.path,
    action?.route,
    action?.href,
    action?.url,
    action?.data?.path,
    action?.data?.route,
  ]
    .map(normalizeActionPath)
    .find(Boolean);
  if (explicitRoute) return explicitRoute;

  const text = `${action?.label || ""} ${action?.prompt || ""} ${action?.intent || ""}`.toLowerCase();
  if (!text.trim()) return null;

  if (text.includes("checkout") || text.includes("place this order") || text.includes("place order")) return ROUTES.CHECKOUT;
  if (text.includes("track") && text.includes("order")) return ROUTES.TRACK_ORDER;
  if (text.includes("order history") || text.includes("reorder") || text.includes("buy this order again")) return ROUTES.ORDER_HISTORY;
  if (text.includes("request list") || text.includes("show my request") || text.includes("requests")) return ROUTES.REQUESTS;
  if (text.includes("saved for later") || text.includes("save for later")) return ROUTES.SAVED_FOR_LATER;
  if (text.includes("recommend")) return ROUTES.RECOMMENDATIONS;
  if (text.includes("support")) return ROUTES.SUPPORT;
  if (text.includes("case") || text.includes("workorder") || text.includes("work order") || text.includes("service")) {
    return pathname.startsWith("/service/") ? "/service/cases" : ROUTES.SERVICES;
  }
  if (text.includes("document") || text.includes("manual") || text.includes("datasheet") || text.includes("knowledge")) return ROUTES.DOCUMENT;
  if (text.includes("machine") || text.includes("find parts")) return ROUTES.MACHINES;
  if (text.includes("cart")) return ROUTES.CART;

  return null;
};

const ChatBot = () => {
  const { t } = useTranslation();
  const { selectedSite, selectedSiteData, siteData, updateSite } = useSite();
  const { cartItems, clearCart, refreshCart } = useCart();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [input, setInput] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [sendMessage, setSendMessage] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [conversationId, setConversationId] = useState("");
  const [chatEnabled, setChatEnabled] = useState(true);
  const [stageIndex, setStageIndex] = useState(0);
  const [initialQuestions, setInitialQuestions] = useState(() =>
    getInitialQuestionsForPath(location?.pathname || window.location.pathname),
  );

  const chatboxRef = useRef(null);
  const activeStreamRef = useRef(null);
  const stageIntervalRef = useRef(null);

  useEffect(() => {
    if (!conversationId) setConversationId(`chip_${Date.now()}`);
  }, [conversationId]);

  useEffect(() => {
    let mounted = true;
    const checkStatus = async () => {
      try {
        const status = await getChatbotStatus();
        if (mounted) setChatEnabled(Boolean(status?.data?.enabled ?? true));
      } catch (_error) {
        if (mounted) setChatEnabled(true);
      }
    };
    checkStatus();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    setInitialQuestions(
      getInitialQuestionsForPath(location?.pathname || window.location.pathname),
    );
  }, [location?.pathname]);

  useEffect(() => {
    if (sendMessage) {
      handleSend(input);
      setSendMessage(false);
    }
  }, [sendMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => {
    if (activeStreamRef.current) activeStreamRef.current.abort();
    if (stageIntervalRef.current) clearInterval(stageIntervalRef.current);
  }, []);

  useEffect(() => {
    if (chatboxRef.current) {
      chatboxRef.current.scrollTop = chatboxRef.current.scrollHeight;
    }
  }, [conversationHistory]);

  const startStageRotation = () => {
    setStageIndex(0);
    if (stageIntervalRef.current) clearInterval(stageIntervalRef.current);
    stageIntervalRef.current = setInterval(() => {
      setStageIndex((prev) => (prev + 1) % STAGE_MESSAGES.length);
    }, 1300);
  };

  const stopStageRotation = () => {
    if (stageIntervalRef.current) {
      clearInterval(stageIntervalRef.current);
      stageIntervalRef.current = null;
    }
  };

  const updateLastAssistantMessage = (updater) => {
    setConversationHistory((prev) => {
      if (!prev.length) return prev;
      const next = [...prev];
      const idx = next.length - 1;
      next[idx] = { ...next[idx], ...updater(next[idx]) };
      return next;
    });
  };

  const buildPayload = (message, sid) => ({
    question: message,
    session_id: sid,
    screen_context: {
      path: window.location.pathname,
      site_number: selectedSite || localStorage.getItem("selectedSite") || undefined,
      site_id: Number(selectedSiteData?.site_id) || undefined,
      site_name: selectedSiteData?.customer_site_name || undefined,
    },
  });

  const mapAddress = (addr = {}, fallbackName = "") => ({
    name: String(addr?.address_name || fallbackName || "").trim(),
    street: [addr?.street1, addr?.street2, addr?.street3].filter(Boolean).join(", ").trim(),
    city: String(addr?.city || "").trim(),
    state: String(addr?.state || "").trim(),
    zip: String(addr?.zip || "").trim(),
    country: String(addr?.country || "").trim(),
  });

  const isAddressComplete = (addr = {}) =>
    ["name", "street", "city", "state", "zip", "country"].every((key) => String(addr?.[key] || "").trim());

  const fetchDefaultAddress = async (type) => {
    const sid = Number(selectedSiteData?.site_id || 0);
    if (!sid) return null;
    try {
      const resp = await getSiteAddressesNew(sid, type);
      const listRaw = resp?.data?.data ?? resp?.data ?? [];
      const list = Array.isArray(listRaw) ? [...listRaw] : [];
      list.sort((a, b) => (b?.is_default ? 1 : 0) - (a?.is_default ? 1 : 0));
      return list[0] || null;
    } catch (_err) {
      return null;
    }
  };

  const askCheckoutDetails = async (defaults = {}, allowEdit = false) => {
    const shipping = { ...(defaults?.shipping_address || {}) };
    const billing = { ...(defaults?.billing_address || {}) };
    const additional = { ...(defaults?.additional_details || {}) };
    const payment = { ...(defaults?.payment_details || {}) };

    const title = allowEdit ? "Update checkout details" : "Confirm checkout details";
    const result = await Swal.fire({
      title,
      width: 760,
      html: `
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;text-align:left;">
          <input id="ship_name" class="swal2-input" placeholder="Shipping name" value="${shipping.name || ""}">
          <input id="bill_name" class="swal2-input" placeholder="Billing name" value="${billing.name || ""}">
          <input id="ship_street" class="swal2-input" placeholder="Shipping street" value="${shipping.street || ""}">
          <input id="bill_street" class="swal2-input" placeholder="Billing street" value="${billing.street || ""}">
          <input id="ship_city" class="swal2-input" placeholder="Shipping city" value="${shipping.city || ""}">
          <input id="bill_city" class="swal2-input" placeholder="Billing city" value="${billing.city || ""}">
          <input id="ship_state" class="swal2-input" placeholder="Shipping state" value="${shipping.state || ""}">
          <input id="bill_state" class="swal2-input" placeholder="Billing state" value="${billing.state || ""}">
          <input id="ship_zip" class="swal2-input" placeholder="Shipping zip" value="${shipping.zip || ""}">
          <input id="bill_zip" class="swal2-input" placeholder="Billing zip" value="${billing.zip || ""}">
          <input id="ship_country" class="swal2-input" placeholder="Shipping country" value="${shipping.country || ""}">
          <input id="bill_country" class="swal2-input" placeholder="Billing country" value="${billing.country || ""}">
          <input id="delivery_terms" class="swal2-input" placeholder="Delivery terms (e.g. DAP)" value="${additional.delivery_terms || ""}">
          <input id="mode_delivery" class="swal2-input" placeholder="Mode of delivery" value="${additional.mode_of_delivery || ""}">
          <select id="ship_complete" class="swal2-select" style="grid-column:span 1;">
            <option value="No" ${(additional.ship_complete || "No") === "No" ? "selected" : ""}>Ship complete: No</option>
            <option value="Yes" ${(additional.ship_complete || "No") === "Yes" ? "selected" : ""}>Ship complete: Yes</option>
          </select>
          <select id="payment_mode" class="swal2-select" style="grid-column:span 1;">
            <option value="PO" ${(payment.payment_mode || "PO") === "PO" ? "selected" : ""}>Payment mode: PO</option>
            <option value="CC" ${(payment.payment_mode || "PO") === "CC" ? "selected" : ""}>Payment mode: CC</option>
          </select>
          <input id="po_number" class="swal2-input" style="grid-column:span 2;" placeholder="PO Number (required for PO mode)" value="${payment.payment_details?.po_number || ""}">
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Place Order",
      preConfirm: () => {
        const out = {
          shipping_address: {
            name: document.getElementById("ship_name").value.trim(),
            street: document.getElementById("ship_street").value.trim(),
            city: document.getElementById("ship_city").value.trim(),
            state: document.getElementById("ship_state").value.trim(),
            zip: document.getElementById("ship_zip").value.trim(),
            country: document.getElementById("ship_country").value.trim(),
          },
          billing_address: {
            name: document.getElementById("bill_name").value.trim(),
            street: document.getElementById("bill_street").value.trim(),
            city: document.getElementById("bill_city").value.trim(),
            state: document.getElementById("bill_state").value.trim(),
            zip: document.getElementById("bill_zip").value.trim(),
            country: document.getElementById("bill_country").value.trim(),
          },
          additional_details: {
            delivery_terms: document.getElementById("delivery_terms").value.trim(),
            ship_complete: document.getElementById("ship_complete").value,
            mode_of_delivery: document.getElementById("mode_delivery").value.trim(),
            freight_number: "",
            order_notes: "",
          },
          payment_details: {
            payment_mode: document.getElementById("payment_mode").value,
            payment_details: {
              po_number: document.getElementById("po_number").value.trim(),
              po_file: "",
              po_filename: "",
            },
          },
        };

        const shippingOk = isAddressComplete(out.shipping_address);
        const billingOk = isAddressComplete(out.billing_address);
        if (!shippingOk || !billingOk) {
          Swal.showValidationMessage("Shipping and billing address fields are required.");
          return null;
        }
        if (!out.additional_details.delivery_terms || !out.additional_details.mode_of_delivery) {
          Swal.showValidationMessage("Delivery terms and mode of delivery are required.");
          return null;
        }
        if (out.payment_details.payment_mode === "PO" && !out.payment_details.payment_details.po_number) {
          Swal.showValidationMessage("PO number is required for PO payment mode.");
          return null;
        }
        return out;
      },
    });

    return result?.isConfirmed ? result.value : null;
  };

  const runPlaceOrderWizard = async (action = {}) => {
    const siteNumber = String(action?.site_number || selectedSite || "").trim();
    const siteId = Number(selectedSiteData?.site_id || 0);
    if (!siteNumber || !siteId) {
      await Swal.fire("Missing Site", "Select a valid site before placing an order.", "warning");
      return;
    }

    const cartForSite = (Array.isArray(cartItems) ? cartItems : []).filter((item) => String(item?.Site || "").trim() === siteNumber);
    if (!cartForSite.length) {
      await Swal.fire("Empty Cart", `Your cart is empty for site ${siteNumber}.`, "info");
      return;
    }

    const shippingDefaultRaw = await fetchDefaultAddress("Shipping");
    const billingDefaultRaw = await fetchDefaultAddress("Billing");
    const shippingDefault = mapAddress(shippingDefaultRaw, selectedSiteData?.address_name_ln || "Shipping");
    const billingDefault = mapAddress(billingDefaultRaw, "Billing");
    const baseDefaults = {
      shipping_address: shippingDefault,
      billing_address: billingDefault,
      additional_details: {
        delivery_terms: "DAP",
        ship_complete: "No",
        mode_of_delivery: "Standard",
        freight_number: "",
        order_notes: "",
      },
      payment_details: {
        payment_mode: "PO",
        payment_details: {
          po_number: "",
          po_file: "",
          po_filename: "",
        },
      },
    };

    const choice = await Swal.fire({
      title: "Place Order",
      text: "Use default checkout values or change required fields?",
      icon: "question",
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: "Use Defaults",
      denyButtonText: "Change Values",
    });
    if (choice.isDismissed) return;

    const checkoutInput = await askCheckoutDetails(baseDefaults, choice.isDenied);
    if (!checkoutInput) return;

    const productIds = cartForSite.map((item) => Number(item?.ProductID || item?.product_id)).filter((id) => Number.isFinite(id) && id > 0);
    const productResp = await getProductsById(productIds, siteNumber);
    const fullProducts = Array.isArray(productResp?.data) ? productResp.data : [];
    const productMap = new Map(fullProducts.map((p) => [Number(p.product_id), p]));
    const currency = String(selectedSiteData?.site_currency || "USD").toLowerCase();

    const cartProducts = cartForSite
      .map((item) => {
        const pid = Number(item?.ProductID || item?.product_id);
        const p = productMap.get(pid);
        if (!p) return null;
        const qty = Math.max(1, Number(item?.Quantity || 1));
        const price = Number(p?.[`price_${currency}`] || 0);
        return {
          product_id: p.product_id,
          product_name: p.product_name,
          product_number: p.product_number,
          ProductQty: qty,
          ProductPrice: price,
          total: (qty * price).toFixed(2),
        };
      })
      .filter(Boolean);

    if (!cartProducts.length) {
      await Swal.fire("No Products", "Could not resolve cart products for order placement.", "error");
      return;
    }

    const productTotal = cartProducts.reduce((sum, p) => sum + Number(p.total || 0), 0);
    const taxes = (productTotal * 0.08).toFixed(2);
    const discounts = (productTotal * 0.05).toFixed(2);
    const shipping = 50.0;
    const total = (productTotal + Number(taxes) - Number(discounts) + shipping).toFixed(2);

    const payload = {
      ...checkoutInput,
      order_details: {
        products: cartProducts,
        taxes,
        shipping,
        discounts,
        total,
        currency,
      },
      selectedSite: siteId,
      loggedin_user: user?.user_id,
      loggedin_user_email: user?.user_email || "",
      loggedin_user_name: `${user?.user_first_name || ""} ${user?.user_last_name || ""}`.trim(),
      loggedin_user_phone: user?.user_phone || "",
    };

    const response = await createOrder(payload);
    if (response?.status && response?.data) {
      await clearCart();
      await refreshCart();
      await Swal.fire("Order Placed", `Order created successfully: ${response?.data?.order_number || ""}`, "success");
      setConversationHistory((prev) => [
        ...prev,
        {
          user: "Place order",
          response: `Done. Order placed successfully${response?.data?.order_number ? `: ${response.data.order_number}` : ""}.`,
          actions: [],
          pendingAction: null,
          data: null,
          toolsUsed: ["prepare_place_order"],
          timestamp: new Date().toISOString(),
        },
      ]);
      return;
    }

    await Swal.fire("Order Failed", response?.data?.message || "Failed to place order.", "error");
  };

  const hydrateProducts = async (rawData) => {
    if (!rawData || !selectedSite) return rawData;
    const rows = [
      ...(Array.isArray(rawData?.products) ? rawData.products : []),
      ...(Array.isArray(rawData?.rows) ? rawData.rows : []),
      ...(Array.isArray(rawData?.items) ? rawData.items : []),
    ];

    const productIdSet = new Set();
    rows.forEach((row) => {
      if (row?.productData?.product_id) productIdSet.add(Number(row.productData.product_id));
      else if (row?.product_id) productIdSet.add(Number(row.product_id));
      else if (row?.ProductID) productIdSet.add(Number(row.ProductID));
    });

    const productIds = Array.from(productIdSet).filter((id) => Number.isFinite(id) && id > 0);
    if (!productIds.length) return rawData;

    try {
      const resp = await getProductsById(productIds, selectedSite);
      const full = Array.isArray(resp?.data) ? resp.data : [];
      const productMap = new Map(full.map((p) => [Number(p.product_id), p]));
      // eslint-disable-next-line no-console
      console.log(
        "[CHIP hydrateProducts] sent ids:", productIds,
        "| data.rows:", (rawData?.rows || []).map((r) => r.product_number || r.sub_name || r.name),
        "| data.products:", (rawData?.products || []).map((r) => r.product_number || r.sub_name),
        "| byids returned:", full.map((p) => p.product_number),
      );
      return { ...rawData, _productMap: productMap };
    } catch (_error) {
      return rawData;
    }
  };

  const applyFinalAssistantResponse = async (payload, fallbackText) => {
    const assistantText =
      payload?.response ||
      payload?.data?.answer ||
      payload?.data?.response ||
      fallbackText ||
      t("UI_TEXT.SORRY_THERE_WAS_AN_ERROR_PROCESSING_YOUR_REQUEST");
    const assistantActions = normalizeAssistantActions(payload?.actions);
    const pendingAction = payload?.pending_action || null;
    const rawData = payload?.data || null;

    // Apply text + actions immediately so the user sees the final response without
    // waiting for the product-hydration API call.
    updateLastAssistantMessage(() => ({
      response: assistantText,
      actions: assistantActions,
      pendingAction,
      data: rawData,
      toolsUsed: payload?.tools_used || [],
      auditId: payload?.audit_id || null,
      feedback: null,
      timestamp: new Date().toISOString(),
    }));

    // Hydrate products in the background; patch only the data field once ready.
    hydrateProducts(rawData).then((hydratedData) => {
      if (hydratedData !== rawData) {
        updateLastAssistantMessage((prev) => ({ ...prev, data: hydratedData }));
      }
    }).catch(() => {});
    if (payload?.session_id) setConversationId(payload.session_id);

      const agentActions = [
        ...(Array.isArray(payload?.agent_actions) ? payload.agent_actions : []),
        ...(Array.isArray(payload?.data?.agent_actions) ? payload.data.agent_actions : []),
      ];
      for (const action of agentActions) {
      const actionType = String(action?.type || "").toLowerCase();
      if (actionType === "refresh_cart") {
        refreshCart();
      } else if (actionType === "switch_site") {
        const nextSite = String(action?.site_number || "").trim();
        if (nextSite) {
          updateSite(nextSite, Array.isArray(siteData) ? siteData : []);
        }
      } else if (actionType === "navigate") {
        const dest = String(action?.path || "").trim();
        if (dest) navigate(dest);
      } else if (actionType === "open_place_order_form") {
        try {
          // eslint-disable-next-line no-await-in-loop
          await runPlaceOrderWizard(action);
        } catch (_err) {
          await Swal.fire("Order Flow Error", "Could not open place-order form.", "error");
        }
      } else if (actionType) {
        window.dispatchEvent(new CustomEvent("chip:agent_action", { detail: action }));
      }
    }
  };

  const handleSend = async (inputQuestion) => {
    const question = String(inputQuestion || "").trim();
    if (!question || isAnalyzing) return;

    const sid = conversationId || `chip_${Date.now()}`;
    setInput("");
    setShowSuggestions(false);
    setIsAnalyzing(true);
    startStageRotation();

    setConversationHistory((prev) => [
      ...prev,
      {
        user: question,
        response: null,
        actions: [],
        pendingAction: null,
        data: null,
        toolsUsed: [],
        timestamp: new Date().toISOString(),
      },
    ]);

    try {
      const payload = buildPayload(question, sid);
      let streamedText = "";

      try {
        const controller = new AbortController();
        activeStreamRef.current = controller;
        await streamChatbot(payload, {
          signal: controller.signal,
          onEvent: async (event) => {
            if (!event) return;
            if (event.type === "chunk") {
              streamedText += String(event.chunk || "");
              updateLastAssistantMessage(() => ({ response: streamedText }));
            }
            if (event.type === "done") {
              await applyFinalAssistantResponse(event, streamedText);
            }
            if (event.type === "error") {
              throw new Error(event.message || "CHIP streaming failed");
            }
          },
        });
      } catch (_streamError) {
        const response = await askChatbot(payload);
        await applyFinalAssistantResponse(response, streamedText);
      } finally {
        activeStreamRef.current = null;
      }
    } catch (_error) {
      updateLastAssistantMessage(() => ({
        response: t("UI_TEXT.SORRY_THERE_WAS_AN_ERROR_PROCESSING_YOUR_REQUEST"),
        actions: [],
        pendingAction: null,
        timestamp: new Date().toISOString(),
      }));
    } finally {
      stopStageRotation();
      setIsAnalyzing(false);
    }
  };

  const handleStopStreaming = () => {
    if (activeStreamRef.current) {
      activeStreamRef.current.abort();
      activeStreamRef.current = null;
    }
    stopStageRotation();
    setIsAnalyzing(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      setSendMessage(true);
    }
  };

  const handleSuggestedQuestion = (question) => {
    setInput(question);
    setSendMessage(true);
  };

  const handleActionClick = (actionInput) => {
    const action = typeof actionInput === "string" ? { prompt: actionInput } : (actionInput || {});
    const type = inferActionType(action);
    // Agentic chips must always go to CHIP — never short-circuit to navigation,
    // because the chip prompt is an instruction for CHIP to execute (e.g. "Add part X to cart"),
    // not a request to navigate somewhere.
    if (type !== "agentic") {
      const route = resolveActionRoute(action, location?.pathname || "");
      if (route) {
        navigate(route);
        return;
      }
    }
    const prompt = action?.prompt || action?.label;
    if (!prompt) return;
    setInput(prompt);
    setSendMessage(true);
  };

  const handleClearResults = async () => {
    if (activeStreamRef.current) {
      activeStreamRef.current.abort();
      activeStreamRef.current = null;
    }
    stopStageRotation();
    const currentSid = conversationId;
    setShowSuggestions(true);
    setConversationHistory([]);
    setConversationId(`chip_${Date.now()}`);
    if (currentSid) {
      try {
        await clearChatbotSession({ session_id: currentSid });
      } catch (_error) {
        // ignore clear failures
      }
    }
  };

  const handleResponseFeedback = async (index, type) => {
    const item = conversationHistory?.[index];
    if (!item?.auditId || item?.feedback) return;

    let correction = "";
    if (type === "not_helpful") {
      const result = await Swal.fire({
        title: "Help Ask CHIP improve",
        text: "What would make this answer more useful?",
        input: "textarea",
        inputPlaceholder: "Type your feedback (optional)",
        showCancelButton: true,
        confirmButtonText: "Submit",
        cancelButtonText: "Skip",
        inputAttributes: {
          maxlength: 2000,
          "aria-label": "Feedback input",
        },
      });
      if (result.isDismissed) return;
      correction = String(result.value || "").trim();
    }

    try {
      await submitChatbotFeedback({
        audit_id: item.auditId,
        feedback: type,
        correction: correction || null,
        session_id: conversationId || null,
        user_message: item.user || null,
        bot_response: item.response || null,
      });
      setConversationHistory((prev) => {
        const next = [...prev];
        if (!next[index]) return prev;
        next[index] = { ...next[index], feedback: type };
        return next;
      });
    } catch (_error) {
      // ignore feedback errors silently in UI
    }
  };

  const renderDataBlocks = (item) => {
    const data = item?.data || {};
    const productMap = data?._productMap;
    const availableToPurchaseRowsRaw = Array.isArray(data?.available_to_purchase) ? data.available_to_purchase : [];
    const requestListRowsRaw = Array.isArray(data?.request_list) ? data.request_list : [];
    const availableToPurchase = availableToPurchaseRowsRaw.map((row) => normalizeRequestRow(row, "available")).filter(Boolean);
    const requestListRows = requestListRowsRaw.map((row) => normalizeRequestRow(row, "request")).filter(Boolean);
    const hasRequestBuckets = availableToPurchase.length > 0 || requestListRows.length > 0;
    const rows = [
      ...(Array.isArray(data?.rows) ? data.rows : []),
      ...(Array.isArray(data?.items) ? data.items : []),
      ...(Array.isArray(data?.products) ? data.products : []),
    ];

    const machineCandidates = [
      ...(Array.isArray(data?.machines) ? data.machines : []),
      ...rows.map((r) => r?.machineData).filter(Boolean),
    ];
    const machines = machineCandidates
      .filter((m) => m?.machine_id || m?.id)
      .slice(0, 4);

    const seenProductKeys = new Set();
    const products = rows
      .map((r) => {
        if (r?.productData) return r.productData;
        const pid = Number(r?.product_id || r?.ProductID);
        if (productMap?.has(pid)) {
          // Use the hydrated product for richer fields (price/image) but keep the
          // machine context from the original row. The by-ids hydration has no
          // machine filter, so it can return an arbitrary machine for a part that
          // belongs to several machines.
          const hydrated = productMap.get(pid);
          return {
            ...hydrated,
            machine_id: r?.machine_id ?? r?.MachineID ?? hydrated?.machine_id,
            machine_number: r?.machine_number ?? hydrated?.machine_number,
          };
        }
        if (r?.product_id && (r?.product_name || r?.product_number)) return r;
        return null;
      })
      .filter(Boolean)
      // De-duplicate: rows merges data.rows + data.items + data.products, and the
      // search can return one row per machine association, so the same part can
      // appear multiple times. Key by product_id (fallback product_number).
      .filter((p) => {
        const key = String(p?.product_id ?? p?.ProductID ?? p?.product_number ?? "").trim();
        if (!key) return true;
        if (seenProductKeys.has(key)) return false;
        seenProductKeys.add(key);
        return true;
      })
      .slice(0, 5);

    const orders = (Array.isArray(data?.orders) ? data.orders : [])
      .map(normalizeOrderEntry)
      .filter(Boolean)
      .slice(0, 4);

    const docs = [
      ...(Array.isArray(data?.documents) ? data.documents : []),
      ...(Array.isArray(data?.matches) ? data.matches : []),
    ].slice(0, 4);
    const cases = [
      ...(Array.isArray(data?.preview) ? data.preview : []),
      ...(Array.isArray(data?.cases) ? data.cases : []),
    ]
      .map((c) => ({
        case_id: c?.case_id || c?.id || c?.ticket_id || "",
        case_number: c?.case_number || c?.ticket_number || c?.number || "",
        title: c?.title || c?.case_title || c?.subject || "",
        status: c?.status || c?.case_status || c?.ticket_status || "",
      }))
      .filter((c) => c.case_number || c.title || c.status || c.case_id)
      .slice(0, 5);
    const sites = Array.isArray(data?.sites) ? data.sites : [];
    const charts = Array.isArray(data?.charts) ? data.charts : [];

    if (!products.length && !machines.length && !orders.length && !docs.length && !cases.length && !sites.length && !charts.length && !availableToPurchase.length && !requestListRows.length) return null;

    const byMachine = (() => {
      if (!hasRequestBuckets) return [];
      const groups = new Map();
      [...requestListRows, ...availableToPurchase].forEach((entry) => {
        const key = String(entry.machine_id || "unknown");
        const existing = groups.get(key) || {
          machine_id: entry.machine_id,
          machine_name: entry.machine_name || "Machine",
          machine_number: entry.machine_number || "N/A",
          request: [],
          available: [],
        };
        if (entry.bucket === "available") existing.available.push(entry.product);
        else existing.request.push(entry.product);
        groups.set(key, existing);
      });
      return Array.from(groups.values()).slice(0, 6);
    })();
    const wantsByMachine = /\b(by machine|machine[-\s]?wise|group(?:ed)? by machine)\b/i.test(String(item?.user || ""))
      || data?.group_by_machine === true
      || String(data?.view || "").toLowerCase() === "by_machine";

    return (
      <Box className="chat-data-block">
        {hasRequestBuckets && wantsByMachine && byMachine.length > 0 && (
          <Box className="chat-entity-group">
            <Typography variant="caption" className="chat-entity-title">By Machine</Typography>
            {byMachine.map((group, idx) => (
              <Box key={`chat_req_machine_${group.machine_id || idx}`} className="chat-entity-group">
                <MachineListItem
                  machine={{
                    machine_id: group.machine_id,
                    machine_name: group.machine_name,
                    machine_number: group.machine_number,
                  }}
                />
                {group.request.length > 0 && (
                  <Box sx={{ pl: 1, pt: 0.5 }}>
                    <Typography variant="caption" className="chat-entity-title">Request List</Typography>
                    {group.request.slice(0, 5).map((p, pIdx) => (
                      <TopProductListItem
                        key={`chat_req_item_${group.machine_id || idx}_${p.product_id || pIdx}`}
                        product={p}
                        machine_id={group.machine_id || p.machine_id || data?.machine_id || 1}
                        source="search_results"
                      />
                    ))}
                  </Box>
                )}
                {group.available.length > 0 && (
                  <Box sx={{ pl: 1, pt: 0.5 }}>
                    <Typography variant="caption" className="chat-entity-title">Available to Purchase</Typography>
                    {group.available.slice(0, 5).map((p, pIdx) => (
                      <TopProductListItem
                        key={`chat_atp_item_${group.machine_id || idx}_${p.product_id || pIdx}`}
                        product={p}
                        machine_id={group.machine_id || p.machine_id || data?.machine_id || 1}
                        source="search_results"
                      />
                    ))}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}

        {hasRequestBuckets && !wantsByMachine && (
          <>
            {requestListRows.length > 0 && (
              <Box className="chat-entity-group">
                <Typography variant="caption" className="chat-entity-title">Request List</Typography>
                {requestListRows.slice(0, 10).map((entry, idx) => (
                  <TopProductListItem
                    key={`chat_req_flat_${entry.product?.product_id || idx}`}
                    product={entry.product}
                    machine_id={entry.machine_id || entry.product?.machine_id || data?.machine_id || 1}
                    source="search_results"
                  />
                ))}
              </Box>
            )}
            {availableToPurchase.length > 0 && (
              <Box className="chat-entity-group">
                <Typography variant="caption" className="chat-entity-title">Available to Purchase</Typography>
                {availableToPurchase.slice(0, 10).map((entry, idx) => (
                  <TopProductListItem
                    key={`chat_atp_flat_${entry.product?.product_id || idx}`}
                    product={entry.product}
                    machine_id={entry.machine_id || entry.product?.machine_id || data?.machine_id || 1}
                    source="search_results"
                  />
                ))}
              </Box>
            )}
          </>
        )}

        {!hasRequestBuckets && products.length > 0 && (
          <Box className="chat-entity-group">
            <Typography variant="caption" className="chat-entity-title">Products</Typography>
            {products.map((p, idx) => (
              <TopProductListItem
                key={`chat_prod_${p.product_id || idx}`}
                product={p}
                machine_id={p.machine_id || data?.machine_id || 1}
                source="search_results"
              />
            ))}
          </Box>
        )}

        {!hasRequestBuckets && machines.length > 0 && (
          <Box className="chat-entity-group">
            <Typography variant="caption" className="chat-entity-title">Machines</Typography>
            {machines.map((m, idx) => (
              <MachineListItem key={`chat_machine_${m.machine_id || idx}`} machine={m} />
            ))}
          </Box>
        )}

        {orders.length > 0 && (
          <Box className="chat-entity-group">
            <Typography variant="caption" className="chat-entity-title">Orders</Typography>
            {orders.map((o, idx) => (
              <OrderListItem key={`chat_order_${o.order_id || idx}`} order={o} />
            ))}
          </Box>
        )}

        {cases.length > 0 && (
          <Box className="chat-entity-group">
            <Typography variant="caption" className="chat-entity-title">Cases</Typography>
            {cases.map((c, idx) => (
              <Typography key={`chat_case_${c.case_id || idx}`} variant="body2" sx={{ py: 0.3 }}>
                {c.case_number || `Case ${idx + 1}`}{c.status ? ` | ${c.status}` : ""}{c.title ? ` | ${c.title}` : ""}
              </Typography>
            ))}
          </Box>
        )}

        {sites.length > 0 && (
          <Box className="chat-entity-group">
            <Typography variant="caption" className="chat-entity-title">Locations</Typography>
            {sites.slice(0, 12).map((s, idx) => (
              <Typography key={`chat_site_${s.site_id || idx}`} variant="body2" sx={{ py: 0.3 }}>
                {s.customer_site_number} | {s.customer_site_name || "Site"}{s.parent_customer_name ? ` | ${s.parent_customer_name}` : ""}
              </Typography>
            ))}
          </Box>
        )}

        {docs.length > 0 && (
          <Box className="chat-entity-group">
            <Typography variant="caption" className="chat-entity-title">Documents</Typography>
            {docs.map((doc, idx) => (
              <DocCard
                key={`chat_doc_${doc.id || idx}`}
                title={doc?.name || doc?.document_title || `Document ${idx + 1}`}
                subtitle={doc?.description || doc?.document_description || ""}
                href={getDocHref(doc)}
                chipLabel={getDocChipLabel(doc)}
              />
            ))}
          </Box>
        )}

        {charts.length > 0 && (
          <Box className="chat-entity-group">
            <Typography variant="caption" className="chat-entity-title">Analytics</Typography>
            {charts.slice(0, 3).map((chart, idx) => (
              <Box key={`chat_chart_${chart.id || idx}`} sx={{ border: "1px solid #ececec", borderRadius: 2, p: 1, mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {chart?.title || `Chart ${idx + 1}`}
                </Typography>
                <ReactECharts option={getChartOption(chart)} style={{ height: 220, width: "100%" }} />
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box className="chatbot-shell">
      <Box className="chatbot-header">
        <Box className="chip-brand">
          <img src={CHIP_ICON} alt="CHIP" className="chip-header-icon" />
          <Typography variant="h6" className="chatbot-title">
            {"Ask CHIP"}
          </Typography>
        </Box>
        <Box />
      </Box>

      <Box className="chatbot-scroll" ref={chatboxRef}>
        {showSuggestions && (
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", rowGap: "16px" }}>
            <Box sx={{ display: "flex", flexDirection: "column", rowGap: "10px", alignItems: "flex-start", pl: 1 }}>
              <AutoAwesomeIcon sx={{ fontSize: 44, color: "#aaa" }} />
              <Typography variant="h3" sx={{ color: "#181818", fontSize: "20px", fontWeight: "500" }}>
                {t("CHAT_SUPPORT.HOW_CAN_I_HELP_YOU_TODAY")}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", rowGap: "6px", alignItems: "flex-start", pl: 1 }}>
              {initialQuestions.map((quest) => (
                <Button
                  key={quest}
                  className="quick-question"
                  variant="outlined"
                  size="small"
                  onClick={() => handleSuggestedQuestion(quest)}
                  startIcon={getIconForPrompt(quest, "question")}
                >
                  {quest}
                </Button>
              ))}
            </Box>
          </Box>
        )}

        {conversationHistory.length > 0 && (
          <Box sx={{ px: 1.5, pb: 2 }}>
            {conversationHistory.map((item, index) => (
              <Box key={index} sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Box sx={{ bgcolor: "primary.main", color: "white", p: 1.5, borderRadius: "16px 16px 0 16px", maxWidth: "75%" }}>
                    <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>{item.user}</Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.75rem", fontStyle: "italic", mt: 0.5, mr: 1 }}>
                    {new Date(item.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 1 }}>
                  {item.response === null ? (
                    <Box className="chip-stage">
                      <img src={CHIP_ICON} alt="CHIP" className="chip-avatar" />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body1" sx={{ animation: `${pulse} 1.4s ease-in-out infinite`, mb: 0.75 }}>
                          {STAGE_MESSAGES[stageIndex]}
                        </Typography>
                        <Box className="chip-thinking-shimmer">
                          <span />
                          <span />
                          <span />
                        </Box>
                      </Box>
                    </Box>
                  ) : item.response ? (
                    <Box sx={{ display: "flex", alignItems: "flex-start", columnGap: 1.2, width: "100%" }}>
                      <img src={CHIP_ICON} alt="CHIP" className="chip-avatar" />
                      <Box className={`assistant-message-wrap ${isAnalyzing && index === conversationHistory.length - 1 ? "assistant-message-typing" : ""}`} sx={{ flex: 1 }}>
                        <ReactMarkdown rehypePlugins={[rehypeRaw]}>{item.response}</ReactMarkdown>
                        {item.pendingAction && (
                          <Typography variant="caption" sx={{ color: "warning.main", display: "block", mt: 1 }}>
                            Working on: {item.pendingAction}
                          </Typography>
                        )}
                        {renderDataBlocks(item)}
                        {Array.isArray(item.actions) && item.actions.length > 0 && (
                          <>
                            <Divider sx={{ my: 1 }} />
                            {(() => {
                              const actions = item.actions.slice(0, 6);
                              const questions = actions.filter((act) => inferActionType(act) === "question");
                              const agentic = actions.filter((act) => inferActionType(act) === "agentic");
                              if (!questions.length && !agentic.length) return null;
                              return (
                                <Box className="chat-actions-wrap">
                                  {questions.length > 0 && (
                                    <Box className="chat-actions-group chat-actions-group-question">
                                      <Box className="chat-actions-head">
                                        <Typography variant="caption" className="chat-actions-title chat-actions-title-question">
                                          <HelpOutlineIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: "text-bottom" }} />
                                          Ask
                                        </Typography>
                                      </Box>
                                      <Typography variant="caption" className="chat-actions-subtitle">
                                        Quick questions you can ask next
                                      </Typography>
                                      <Box className="chat-actions-row">
                                        {questions.map((act, idx) => (
                                          <Button
                                            key={`${act.label || "question"}_q_${idx}`}
                                            size="small"
                                            variant="outlined"
                                            className="chat-action-chip chat-action-chip-question"
                                            onClick={() => handleActionClick(act)}
                                            startIcon={getIconForPrompt(act.prompt || act.label, "question")}
                                          >
                                            {act.label || act.prompt || "Question"}
                                          </Button>
                                        ))}
                                      </Box>
                                    </Box>
                                  )}

                                  {agentic.length > 0 && (
                                    <Box className="chat-actions-group chat-actions-group-agentic">
                                      <Box className="chat-actions-head">
                                        <Typography variant="caption" className="chat-actions-title chat-actions-title-agentic">
                                          <BoltIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: "text-bottom" }} />
                                          Shortcuts
                                        </Typography>
                                      </Box>
                                      <Typography variant="caption" className="chat-actions-subtitle">
                                        Useful actions you can run now
                                      </Typography>
                                      <Box className="chat-actions-row">
                                        {agentic.map((act, idx) => (
                                          <Button
                                            key={`${act.label || "action"}_a_${idx}`}
                                            size="small"
                                            variant="outlined"
                                            className="chat-action-chip chat-action-chip-agentic"
                                            onClick={() => handleActionClick(act)}
                                            startIcon={getIconForPrompt(act.prompt || act.label, "agentic")}
                                          >
                                            {act.label || act.prompt || "Action"}
                                          </Button>
                                        ))}
                                      </Box>
                                    </Box>
                                  )}
                                </Box>
                              );
                            })()}
                          </>
                        )}
                        {item.auditId && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1 }}>
                            <IconButton
                              size="small"
                              color={item.feedback === "helpful" ? "success" : "default"}
                              disabled={Boolean(item.feedback)}
                              onClick={() => handleResponseFeedback(index, "helpful")}
                              title="Helpful"
                              sx={{
                                color: item.feedback === "helpful" ? "success.dark" : "inherit",
                                opacity: item.feedback === "not_helpful" ? 0.55 : 1,
                                transition: "all 0.18s ease",
                              }}
                            >
                              {item.feedback === "helpful" ? <FaThumbsUp size={14} /> : <FaRegThumbsUp size={14} />}
                            </IconButton>
                            <IconButton
                              size="small"
                              color={item.feedback === "not_helpful" ? "error" : "default"}
                              disabled={Boolean(item.feedback)}
                              onClick={() => handleResponseFeedback(index, "not_helpful")}
                              title="Not Helpful"
                              sx={{
                                color: item.feedback === "not_helpful" ? "error.dark" : "inherit",
                                opacity: item.feedback === "helpful" ? 0.55 : 1,
                                transition: "all 0.18s ease",
                              }}
                            >
                              {item.feedback === "not_helpful" ? <FaThumbsDown size={14} /> : <FaRegThumbsDown size={14} />}
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="body1" color="error">
                      {t("ERRORS.ERROR_GENERATING_RESPONSE_PLEASE_TRY_AGAIN")}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Box className="chatbot-composer">
        <Stack direction="column" spacing={1}>
          {!chatEnabled && (
            <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
              Assistant is temporarily unavailable. Try again shortly.
            </Typography>
          )}
          <TextField
            fullWidth
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about orders, parts, documents, or your cart"
            disabled={!chatEnabled}
            InputProps={{
              endAdornment: (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Tooltip title={isAnalyzing ? "Stop generating" : "New chat"} arrow>
                    <span>
                      <IconButton
                        onClick={isAnalyzing ? handleStopStreaming : handleClearResults}
                        disabled={!chatEnabled}
                        color={isAnalyzing ? "error" : "primary"}
                        sx={{ borderRadius: "50%", p: "8px" }}
                      >
                        {isAnalyzing ? (
                          <StopIcon sx={{ fontSize: "20px" }} />
                        ) : (
                          <AddCircleIcon sx={{ fontSize: "20px" }} />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <IconButton
                    onClick={() => setSendMessage(true)}
                    disabled={!input.trim() || !chatEnabled || isAnalyzing}
                    color="primary"
                    sx={{
                      bgcolor: "primary.main",
                      color: "white",
                      borderRadius: "50%",
                      p: "8px",
                      "&:hover": { bgcolor: "primary.dark", transform: "scale(1.05)" },
                      "&.Mui-disabled": { bgcolor: "grey.300" },
                      transition: "all 0.2s ease",
                    }}
                  >
                    <SendIcon sx={{ fontSize: "18px" }} />
                  </IconButton>
                </Box>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "24px",
                backgroundColor: "white",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                minHeight: "48px",
              },
              "& .MuiInputBase-input": {
                px: "16px",
                fontSize: "14px",
              },
            }}
          />
        </Stack>
      </Box>
    </Box>
  );
};

export default ChatBot;
